import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Header,
  Section,
  FlexBox
} from '../ui/layout';
import {
  Button
} from '../ui/form';
import {
  Card,
  TableContainer,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  EmptyState,
  EmptyStateTitle,
  EmptyStateText,
  LoadingSpinner
} from '../ui/data-display';
import {
  Title
} from '../ui/common';
import {
  ModernTabsContainer,
  ModernTab
} from '../ui/navigation';

interface Company {
  _id: string;
  name: string;
  industry?: string;
  headquarters?: string;
  website?: string;
}

interface Contact {
  _id: string;
  first_name: string;
  last_name: string;
  email: string;
  sales_rep_id?: {
    firstName: string;
    lastName: string;
  };
}

const DevManagement: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const getInitialParam = (key: string, defaultVal: string = '') => {
    return new URLSearchParams(location.search).get(key) || defaultVal;
  };

  const [activeTab, setActiveTab] = useState<'companies' | 'contacts'>(() => {
    const tab = getInitialParam('tab');
    return tab === 'contacts' ? 'contacts' : 'companies';
  });
  const [companies, setCompanies] = useState<Company[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);

  // Sync tab to URL
  useEffect(() => {
    const params = new URLSearchParams();
    if (activeTab !== 'companies') params.set('tab', activeTab);
    navigate(`?${params.toString()}`, { replace: true });
  }, [activeTab]);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      if (activeTab === 'companies') {
        const res = await axios.get('/api/dev/companies/no-contacts', config);
        setCompanies(res.data);
      } else {
        const res = await axios.get('/api/dev/contacts/no-company', config);
        setContacts(res.data);
      }
    } catch (err) {
      console.error('Error fetching dev data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAllOrphanedCompanies = async () => {
    if (!window.confirm(`Sei sicuro di voler eliminare tutte le ${companies.length} aziende senza contatti? QUESTA AZIONE È IRREVERSIBILE.`)) {
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      await axios.delete('/api/dev/companies/no-contacts', {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
      alert('Aziende eliminate con successo');
    } catch (err) {
      console.error('Error deleting orphaned companies:', err);
      alert('Errore durante l\'eliminazione delle aziende');
      setLoading(false);
    }
  };

  const renderCompanies = () => {
    if (loading) return <LoadingSpinner />;
    if (companies.length === 0) {
      return (
        <EmptyState>
          <EmptyStateTitle>Nessuna azienda orfana</EmptyStateTitle>
          <EmptyStateText>Tutte le aziende hanno almeno un contatto associato.</EmptyStateText>
        </EmptyState>
      );
    }

    return (
      <TableContainer>
        <FlexBox justifyContent="flex-end" padding="1rem">
          <Button 
            variant="danger" 
            onClick={handleDeleteAllOrphanedCompanies}
            disabled={loading}
          >
            Elimina Tutte ({companies.length})
          </Button>
        </FlexBox>
        <thead>
          <TableRow>
            <TableHeader>Nome Azienda</TableHeader>
            <TableHeader>Industria</TableHeader>
            <TableHeader>Sede</TableHeader>
            <TableHeader>Sito Web</TableHeader>
          </TableRow>
        </thead>
        <TableBody>
          {companies.map((company) => (
            <TableRow key={company._id}>
              <TableCell>{company.name}</TableCell>
              <TableCell>{company.industry || '-'}</TableCell>
              <TableCell>{company.headquarters || '-'}</TableCell>
              <TableCell>
                {company.website ? (
                  <a href={company.website} target="_blank" rel="noopener noreferrer">
                    {company.website}
                  </a>
                ) : '-'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </TableContainer>
    );
  };

  const renderContacts = () => {
    if (loading) return <LoadingSpinner />;
    if (contacts.length === 0) {
      return (
        <EmptyState>
          <EmptyStateTitle>Nessun contatto orfano</EmptyStateTitle>
          <EmptyStateText>Tutti i contatti sono associati a un'azienda.</EmptyStateText>
        </EmptyState>
      );
    }

    return (
      <TableContainer>
        <thead>
          <TableRow>
            <TableHeader>Nome</TableHeader>
            <TableHeader>Cognome</TableHeader>
            <TableHeader>Email</TableHeader>
            <TableHeader>Sales Rep</TableHeader>
          </TableRow>
        </thead>
        <TableBody>
          {contacts.map((contact) => (
            <TableRow key={contact._id}>
              <TableCell>{contact.first_name}</TableCell>
              <TableCell>{contact.last_name}</TableCell>
              <TableCell>{contact.email}</TableCell>
              <TableCell>
                {contact.sales_rep_id 
                  ? `${contact.sales_rep_id.firstName} ${contact.sales_rep_id.lastName}`
                  : '-'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </TableContainer>
    );
  };

  return (
    <Container>
      <Header>
        <Title>Dev Tools</Title>
      </Header>

      <Section>
        <Card>
          <ModernTabsContainer>
            <ModernTab
              active={activeTab === 'companies'}
              onClick={() => setActiveTab('companies')}
            >
              Aziende senza contatti ({loading && activeTab === 'companies' ? '...' : companies.length})
            </ModernTab>
            <ModernTab
              active={activeTab === 'contacts'}
              onClick={() => setActiveTab('contacts')}
            >
              Contatti senza azienda ({loading && activeTab === 'contacts' ? '...' : contacts.length})
            </ModernTab>
          </ModernTabsContainer>

          <div style={{ padding: '20px' }}>
            {activeTab === 'companies' ? renderCompanies() : renderContacts()}
          </div>
        </Card>
      </Section>
    </Container>
  );
};

export default DevManagement;
